// ─── Chin Risk Controller ──────────────────────────────────
// Pure risk evaluation logic for the Chin Bot Play flow.
// Checks 4 rules before any order is sent to cTrader:
//
//   Rule 1 — Spread: current spread must be ≤ 3 pips
//            (NOT applied to XAUUSD, XAGUSD, XTIUSD)
//   Rule 2 — Max Orders: total open positions < maxOrders (per symbol)
//   Rule 3 — Single Symbol: only 1 symbol may have active positions
//   Rule 4 — After Hours: after 23:00 GMT+7 (16:00 UTC) daily,
//            all running engines auto-shutdown to OFF
//
// When Rule 2, 3, or 4 is violated, ALL symbols must be reset to OFF
// and the workspace persisted to Supabase immediately.

import { getPipSize } from './pip-calculator';

// ─── Types ──────────────────────────────────────────────────

export interface RiskPosition {
  positionId: number;
  symbolId: number;
  symbolName: string;
  tradeSide: 'BUY' | 'SELL';
  volume: number;
}

export interface RiskSpreadInfo {
  symbolId: number;
  symbolName: string;
  bid: number | null;
  ask: number | null;
  spreadPips: number | null;
}

export interface RiskSymbolConfig {
  conditionPrice: string;
  maxOrders: string;
  direction: 'buy' | 'sell' | null;
}

export interface RiskCheckResult {
  allowed: boolean;
  blockedBy: 'none' | 'spread' | 'maxOrders' | 'singleSymbol' | 'afterHours' | 'noPrice' | 'noDirection';
  reason: string;
  /** When true, ALL symbols should be reset to OFF immediately */
  shouldResetAll: boolean;
}

export interface RiskEvaluation {
  /** Per-symbol check results */
  checks: Record<number, RiskCheckResult>;
  /** Symbols that currently have open positions */
  symbolsWithPositions: number[];
  /** Total position count per symbol */
  positionCountBySymbol: Record<number, number>;
  /** Whether a full workspace reset is needed (Rule 2, 3, or 4 triggered) */
  needsFullReset: boolean;
  /** Human-readable reason for full reset */
  resetReason: string | null;
}

// ─── Constants ──────────────────────────────────────────────

const MAX_SPREAD_PIPS = 3;

/** Daily shutdown hour in UTC (23:00 GMT+7 = 16:00 UTC) */
const AFTER_HOURS_UTC_HOUR = 16;

/**
 * Returns true if the symbol is one of the metal/oil pairs where Rule 1
 * (spread limit) is intentionally NOT applied: XAUUSD, XAGUSD, XTIUSD.
 */
function isMetalsSymbol(symbolName: string): boolean {
  const upper = symbolName.toUpperCase();
  return upper.startsWith('XAU') || upper.startsWith('XAG') || upper.startsWith('XTI');
}

// ─── Pure Functions ─────────────────────────────────────────

/**
 * Calculate spread in pips for a symbol.
 */
export function calcSpreadPips(
  bid: number | null,
  ask: number | null,
  symbolName: string,
  pipPosition?: number | null,
): number | null {
  if (bid === null || ask === null || bid <= 0 || ask <= 0) return null;
  const rawSpread = ask - bid;
  if (rawSpread <= 0) return 0;
  const pipSize = getPipSize(symbolName, pipPosition);
  return rawSpread / pipSize;
}

/**
 * Check if current time is past the daily shutdown hour (23:00 GMT+7 = 16:00 UTC).
 * Uses the current system clock. Returns the ISO date string (YYYY-MM-DD)
 * of the "trading day" if we're in after-hours, or null if before shutdown.
 *
 * Trading day = UTC date when shutdown occurs.
 * If it's 16:00+ UTC, today's UTC date is the shutdown day.
 * If it's before 16:00 UTC, we haven't reached shutdown yet.
 */
export function getAfterHoursDate(now?: Date): string | null {
  const d = now || new Date();
  const hoursUTC = d.getUTCHours();
  if (hoursUTC < AFTER_HOURS_UTC_HOUR) return null;
  return d.toISOString().slice(0, 10); // "YYYY-MM-DD"
}

/**
 * Evaluate all risk rules for every symbol in the workspace.
 * Pure function — no side effects, no Supabase calls.
 *
 * @param positions - Currently open positions
 * @param spreadInfo - Per-symbol spread data from engine stream
 * @param symbolConfigs - Per-symbol workspace config (direction, conditionPrice, maxOrders)
 * @param activeSymbolIds - Symbol IDs currently in the workspace
 * @param afterHoursResetFiredDate - ISO date string of the last after-hours reset that fired.
 *   If this matches today's after-hours date, Rule 4 won't trigger again.
 *   Pass empty string if no reset has fired today.
 */
export function evaluateRisk(
  positions: RiskPosition[],
  spreadInfo: Record<number, RiskSpreadInfo>,
  symbolConfigs: Record<number, RiskSymbolConfig>,
  activeSymbolIds: number[],
  afterHoursResetFiredDate: string = '',
  rule4Enabled: boolean = true,
): RiskEvaluation {
  // ─── Count positions per symbol ──────────────────────────
  const positionCountBySymbol: Record<number, number> = {};
  const symbolsWithPositions: number[] = [];

  for (const pos of positions) {
    const sid = pos.symbolId;
    positionCountBySymbol[sid] = (positionCountBySymbol[sid] || 0) + 1;
  }

  // Build unique list of symbols that have positions
  for (const sid of Object.keys(positionCountBySymbol)) {
    const numSid = Number(sid);
    if (positionCountBySymbol[numSid] > 0 && !symbolsWithPositions.includes(numSid)) {
      symbolsWithPositions.push(numSid);
    }
  }

  // ─── Rule 4: After Hours Check ──────────────────────────
  // This runs BEFORE per-symbol checks so it can block everything.
  // Only fires ONCE per day — tracked via afterHoursResetFiredDate.
  const afterHoursDate = getAfterHoursDate();
  const isAfterHours = rule4Enabled && afterHoursDate !== null;
  const afterHoursAlreadyFired = isAfterHours && afterHoursDate === afterHoursResetFiredDate;

  let needsFullReset = false;
  let resetReason: string | null = null;

  // ─── Evaluate per-symbol ─────────────────────────────────
  const checks: Record<number, RiskCheckResult> = {};

  // Rule 3 helper
  const hasAnyPosition = symbolsWithPositions.length > 0;

  for (const symId of activeSymbolIds) {
    const config = symbolConfigs[symId] || { conditionPrice: '', maxOrders: '', direction: null };
    const spread = spreadInfo[symId];
    const posCount = positionCountBySymbol[symId] || 0;
    const maxOrders = parseInt(config.maxOrders, 10) || 0;
    const hasPositions = posCount > 0;

    // ─── Rule 4: After Hours — block ALL symbols ──────────
    // If we're past 23:00 GMT+7 AND the reset hasn't fired today,
    // block everything and trigger a full reset.
    // Once the reset has fired, symbols can be re-enabled normally
    // (user setting up for next day).
    if (isAfterHours && !afterHoursAlreadyFired) {
      checks[symId] = {
        allowed: false,
        blockedBy: 'afterHours',
        reason: 'Daily shutdown: trading closed after 23:00 GMT+7. All engines auto-off.',
        shouldResetAll: true,
      };
      needsFullReset = true;
      if (!resetReason) {
        resetReason = 'Daily shutdown at 23:00 GMT+7 — turning off all running engines';
      }
      continue;
    }

    // ─── Rule 3: Single Symbol — block OTHER symbols ──────
    // Only applies when this symbol itself has NO positions
    // but another symbol DOES.
    if (hasAnyPosition && !hasPositions) {
      checks[symId] = {
        allowed: false,
        blockedBy: 'singleSymbol',
        reason: `Cannot trade: positions active on ${symbolsWithPositions.map((sid) => spreadInfo[sid]?.symbolName || `#${sid}`).join(', ')}. Only 1 symbol at a time.`,
        shouldResetAll: false,
      };
      continue;
    }

    // ─── Rule 1: Spread check ────────────────────────────
    // SKIPPED for metals (XAUUSD, XAGUSD, XTIUSD) — spread limit not applied.
    const symbolName = spread?.symbolName || '';
    const skipSpread = isMetalsSymbol(symbolName);
    if (!skipSpread && spread && spread.spreadPips !== null && spread.spreadPips > MAX_SPREAD_PIPS) {
      checks[symId] = {
        allowed: false,
        blockedBy: 'spread',
        reason: `Spread too high: ${spread.spreadPips.toFixed(1)} pips (max ${MAX_SPREAD_PIPS} pips)`,
        shouldResetAll: false,
      };
      continue;
    }

    // ─── Rule 2: Max Orders check ────────────────────────
    // Only applies when this symbol HAS positions and count >= maxOrders
    if (hasPositions && maxOrders > 0 && posCount >= maxOrders) {
      checks[symId] = {
        allowed: false,
        blockedBy: 'maxOrders',
        reason: `Max orders reached: ${posCount}/${maxOrders} positions open`,
        shouldResetAll: true,
      };
      needsFullReset = true;
      if (!resetReason) {
        resetReason = `Max orders: ${spread?.symbolName || `Symbol #${symId}`} has ${posCount}/${maxOrders} positions`;
      }
      continue;
    }

    // ─── All checks passed ────────────────────────────────
    checks[symId] = {
      allowed: true,
      blockedBy: 'none',
      reason: 'OK',
      shouldResetAll: false,
    };
  }

  return {
    checks,
    symbolsWithPositions,
    positionCountBySymbol,
    needsFullReset,
    resetReason,
  };
}
